import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recurrenceService } from '@services/recurrenceService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'recurrences';

export const useRecurrences = () => {
  const queryClient = useQueryClient();

  const {
    data: recurrences = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: recurrenceService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: recurrenceService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Recorrência criada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar recorrência');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ recurrenceId, data }) => recurrenceService.update(recurrenceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Recorrência atualizada!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: recurrenceService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Recorrência excluída!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao excluir');
    },
  });

  const pauseMutation = useMutation({
    mutationFn: recurrenceService.pause,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Recorrência pausada!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao pausar');
    },
  });

  const resumeMutation = useMutation({
    mutationFn: recurrenceService.resume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Recorrência retomada!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao retomar');
    },
  });

  // Separar por tipo
  const incomeRecurrences = recurrences.filter(r => r.type === 'income');
  const expenseRecurrences = recurrences.filter(r => r.type === 'expense');

  // Calcular totais
  const totalIncome = incomeRecurrences.reduce((sum, r) => sum + Math.abs(r.amount), 0);
  const totalExpense = expenseRecurrences.reduce((sum, r) => sum + Math.abs(r.amount), 0);

  return {
    recurrences,
    incomeRecurrences,
    expenseRecurrences,
    totalIncome,
    totalExpense,
    balance: totalIncome - totalExpense,
    isLoading,
    error,
    refetch,
    createRecurrence: createMutation.mutateAsync,
    updateRecurrence: updateMutation.mutateAsync,
    deleteRecurrence: deleteMutation.mutateAsync,
    pauseRecurrence: pauseMutation.mutateAsync,
    resumeRecurrence: resumeMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export default useRecurrences;
